import { Component, ComponentInterface, h, Host, JSX, Listen, Prop } from '@stencil/core';
import { SharedDirectionService } from '../../global/workbench-global';
import { DEFAULT_WORKBENCH_THEME, WorkbenchTheme } from '../../shared/themes';
import { LinkVariant, LinkSize } from './link.types';

/**
 * Text links should take the user to a new place or change the view of the page the user is seeing. The structure of
 * links depends on the context and their purpose. The specification is valid for dark and light themes.
 *
 * @slot - Text content of the link
 * @slot icon - Optional wb-icon element
 */
@Component({
  tag: 'wb-link',
  styleUrl: 'link.scss',
  shadow: true,
})
export class LinkComponent implements ComponentInterface {
  /** Link variant (inline links inherit typography from parent) */
  @Prop({ reflect: true })
  public variant!: LinkVariant;

  /** Link size */
  @Prop({ reflect: true })
  public size: LinkSize = 'l';

  /** The color scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  /** Passed through to internal `<a>` */
  @Prop()
  public href: string;

  /** Passed through to internal `<a>` */
  @Prop()
  public download: any;

  /** Passed through to internal `<a>` */
  @Prop()
  public referrerPolicy: ReferrerPolicy;

  /** Passed through to internal `<a>` */
  @Prop()
  public rel: string;

  /** Passed through to internal `<a>` */
  @Prop()
  public target: string;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  public connectedCallback(): void {
    this.updateDirection();
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  public render(): JSX.Element {
    return (
      <Host class="wb-link">
        <a href={this.href} download={this.download} referrerPolicy={this.referrerPolicy} rel={this.rel} target={this.target}>
          <slot name="icon" />
          <slot />
        </a>
      </Host>
    );
  }
}
